import { Button, Group, Menu } from "@mantine/core";

import { IconCheck, IconChevronDown } from "@tabler/icons-react";

import { StatementStatus } from "@/billing/components/StatementsTab/constants/statements";
import { TextWrapper } from "@/components/TextWrapper";
import { slugify } from "@/utils/strings";

import {
  STATUS_FILTER_ALL_LABEL,
  STATUS_FILTER_CHECK_SIZE,
  STATUS_FILTER_CHEVRON_SIZE,
  STATUS_FILTER_MENU_WIDTH,
  STATUS_FILTER_OPTION_HEIGHT,
  STATUS_FILTER_OPTIONS,
} from "./constants/open-statements";

interface OpenStatementsStatusFilterProps {
  statuses: StatementStatus[];
  onChange: (statuses: StatementStatus[]) => void;
  /** Test id for the dropdown's button, which the Menu renders internally. */
  targetTestId?: string;
}

function resolveLabel(statuses: StatementStatus[]): string {
  if (statuses.length === 0 || statuses.length === STATUS_FILTER_OPTIONS.length) {
    return STATUS_FILTER_ALL_LABEL;
  }
  if (statuses.length === 1) return statuses[0];
  return `${statuses.length} selected`;
}

interface StatusFilterOptionProps {
  label: string;
  checked: boolean;
  onSelect: () => void;
  testId: string;
}

/**
 * One row of the dropdown: the label on the left, and a burgundy check on the right while selected.
 * The design marks the selection with a check rather than a checkbox, so the whole row is the
 * control and the check is decorative — `aria-checked` carries the state for assistive tech.
 */
function StatusFilterOption({ label, checked, onSelect, testId }: StatusFilterOptionProps) {
  return (
    <Menu.Item
      px="lg"
      onClick={onSelect}
      data-testid={testId}
      aria-checked={checked}
      role="menuitemcheckbox"
      h={STATUS_FILTER_OPTION_HEIGHT}
      style={{ borderBottom: "1px solid var(--mantine-color-cream-2)", borderRadius: 0 }}
    >
      <Group gap="xs" wrap="nowrap" justify="space-between">
        <TextWrapper fw={600} size="sm" c="dark.9">
          {label}
        </TextWrapper>
        {checked && (
          <IconCheck
            aria-hidden
            size={STATUS_FILTER_CHECK_SIZE}
            color="var(--mantine-color-burgundy-9)"
          />
        )}
      </Group>
    </Menu.Item>
  );
}

export default function OpenStatementsStatusFilter({
  statuses,
  onChange,
  targetTestId = "open-statements-scope-filter",
}: OpenStatementsStatusFilterProps) {
  // Kept in the option order rather than click order, so the single-selection label and the query
  // fragments don't depend on which row the user happened to pick first.
  const handleToggle = (status: StatementStatus) => {
    const selected = new Set(statuses);
    if (selected.has(status)) {
      selected.delete(status);
    } else {
      selected.add(status);
    }
    onChange(STATUS_FILTER_OPTIONS.filter((option) => selected.has(option)));
  };

  const isAllSelected = statuses.length === 0;

  return (
    <Menu shadow="sm" withinPortal position="bottom-start" closeOnItemClick={false}>
      <Menu.Target>
        <Button
          size="xs"
          variant="outline"
          color="burgundy.9"
          data-testid={targetTestId}
          leftSection={<IconChevronDown aria-hidden size={STATUS_FILTER_CHEVRON_SIZE} />}
        >
          {resolveLabel(statuses)}
        </Button>
      </Menu.Target>
      <Menu.Dropdown p={0} w={STATUS_FILTER_MENU_WIDTH}>
        <StatusFilterOption
          checked={isAllSelected}
          onSelect={() => onChange([])}
          label={STATUS_FILTER_ALL_LABEL}
          testId="open-statements-scope-filter-option-all"
        />
        {STATUS_FILTER_OPTIONS.map((status) => (
          <StatusFilterOption
            key={status}
            label={status}
            checked={statuses.includes(status)}
            onSelect={() => handleToggle(status)}
            testId={`open-statements-scope-filter-option-${slugify(status)}`}
          />
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}
